import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiCopy, FiCheck, FiExternalLink, FiTrash2, FiInbox, FiSend, FiPlus } from "react-icons/fi";
import { transfersApi } from "../../services/transfersApi";
import { MAIN_URL } from "../../utils/appHost";
import { formatBytes } from "../../components/share/fileKinds";

export function useExpiryLabel() {
  const { t, i18n } = useTranslation();
  return (iso) => {
    if (!iso) return t("share.dashboard.never");
    const diff = new Date(iso) - Date.now();
    if (diff <= 0) return t("share.dashboard.expired_on", { date: new Date(iso).toLocaleDateString(i18n.language) });
    const hours = Math.floor(diff / 3600000);
    if (hours < 24) return t("share.dashboard.expires_hours", { count: Math.max(1, hours) });
    return t("share.dashboard.expires_days", { count: Math.ceil(hours / 24) });
  };
}

function TransferRow({ transfer, onDelete, expired }) {
  const { t, i18n } = useTranslation();
  const expiryLabel = useExpiryLabel();
  const [copied, setCopied] = useState(false);
  const soon = transfer.expires_at && new Date(transfer.expires_at) - Date.now() < 24 * 3600000;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(transfer.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div className="shr-row">
      <div style={{ minWidth: 0 }}>
        <Link to={`/transfers/${transfer.transfer_id}`} className="shr-row-title" title={transfer.subject}>
          <span className={`shr-badge ${transfer.kind}`}>
            {transfer.kind === "send" ? <FiSend size={10} /> : <FiInbox size={10} />}
            {t(`share.dashboard.kind_${transfer.kind}`)}
          </span>
          {transfer.subject}
        </Link>
        <div className="shr-file-meta">{new Date(transfer.created_at).toLocaleDateString(i18n.language)}</div>
      </div>
      <div className="shr-hide-sm">{formatBytes(transfer.total_bytes)}</div>
      <div className="shr-hide-sm">
        {t("share.dashboard.files", { count: transfer.file_count })}
        {transfer.kind === "send" && (
          <div className="shr-file-meta">{t("share.dashboard.downloads", { count: transfer.download_count })}</div>
        )}
      </div>
      <div className="shr-hide-sm">
        {soon && !expired ? <span className="shr-badge warn">{expiryLabel(transfer.expires_at)}</span> : expiryLabel(transfer.expires_at)}
      </div>
      <div className="shr-row-actions">
        {!expired && (
          <>
            <button type="button" className="shr-icon-btn" onClick={copy} title={t("share.link.copy")} aria-label={t("share.link.copy")}>
              {copied ? <FiCheck size={15} /> : <FiCopy size={15} />}
            </button>
            <a className="shr-icon-btn" href={transfer.link} target="_blank" rel="noreferrer" title={t("share.dashboard.open")} aria-label={t("share.dashboard.open")}>
              <FiExternalLink size={15} />
            </a>
          </>
        )}
        <button type="button" className="shr-icon-btn" onClick={() => onDelete(transfer)} title={t("share.dashboard.delete")} aria-label={t("share.dashboard.delete")}>
          <FiTrash2 size={15} />
        </button>
      </div>
    </div>
  );
}

/** Files manager: storage used, file count, active / expired transfers. */
export default function ShareDashboard() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("active");
  const [stats, setStats] = useState(null);
  const [transfers, setTransfers] = useState(null);

  const load = useCallback(() => {
    transfersApi.stats().then((res) => setStats(res.data)).catch(() => {});
    setTransfers(null);
    transfersApi
      .list(tab)
      .then((res) => setTransfers(res.data.transfers))
      .catch(() => setTransfers([]));
  }, [tab]);

  useEffect(load, [load]);

  const handleDelete = async (transfer) => {
    if (!window.confirm(t("share.dashboard.delete_confirm", { subject: transfer.subject }))) return;
    await transfersApi.remove(transfer.transfer_id).catch(() => {});
    load();
  };

  const usedPct = stats?.quota_bytes ? Math.min(100, (stats.used_bytes / stats.quota_bytes) * 100) : 0;

  return (
    <>
      <div className="shr-page-head">
        <div>
          <h1 className="shr-title">{t("share.dashboard.title")}</h1>
          <p className="shr-muted">{t("share.dashboard.subtitle")}</p>
        </div>
        <Link to="/" className="shr-btn primary"><FiPlus /> {t("share.home.new_transfer")}</Link>
      </div>

      <div className="shr-stats">
        <div className="shr-stat">
          <div className="shr-stat-label">{t("share.dashboard.storage")}</div>
          <div className="shr-stat-value">
            {formatBytes(stats?.used_bytes || 0)}
            <span className="shr-muted" style={{ fontSize: "0.9rem", fontWeight: 500 }}> / {formatBytes(stats?.quota_bytes || 0)}</span>
          </div>
          <div className="shr-progress lg" style={{ marginTop: 10 }}>
            <span style={{ width: `${usedPct}%`, background: usedPct > 90 ? "#ef4444" : undefined }} />
          </div>
          {usedPct > 80 && (
            <a className="shr-upgrade-link" href={`${MAIN_URL}/plans`}>{t("share.errors.upgrade")}</a>
          )}
        </div>
        <div className="shr-stat">
          <div className="shr-stat-label">{t("share.dashboard.active")}</div>
          <div className="shr-stat-value">{stats?.active_transfers ?? "–"}</div>
        </div>
        <div className="shr-stat">
          <div className="shr-stat-label">{t("share.dashboard.file_count")}</div>
          <div className="shr-stat-value">{stats?.file_count ?? "–"}</div>
        </div>
        <div className="shr-stat">
          <div className="shr-stat-label">{t("share.dashboard.expiring_soon")}</div>
          <div className="shr-stat-value">{stats?.expiring_soon ?? "–"}</div>
          <div className="shr-stat-sub">{t("share.dashboard.within_24h")}</div>
        </div>
      </div>

      <div className="shr-tabs" role="tablist">
        {["active", "expired"].map((key) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? "active" : ""} onClick={() => setTab(key)}>
            {t(`share.dashboard.tab_${key}`)}
          </button>
        ))}
      </div>

      {transfers === null ? (
        <div className="shr-center" style={{ minHeight: 200 }}><div className="shr-spinner" /></div>
      ) : transfers.length === 0 ? (
        <div className="shr-table shr-empty">
          <div className="shr-empty-icon"><FiSend size={22} /></div>
          <p className="shr-muted">{tab === "active" ? t("share.dashboard.empty_active") : t("share.dashboard.empty_expired")}</p>
        </div>
      ) : (
        <div className="shr-table">
          <div className="shr-row head">
            <div>{t("share.dashboard.col_transfer")}</div>
            <div>{t("share.dashboard.col_size")}</div>
            <div>{t("share.dashboard.col_files")}</div>
            <div>{t("share.dashboard.col_expiry")}</div>
            <div />
          </div>
          {transfers.map((tr) => (
            <TransferRow key={tr.transfer_id} transfer={tr} onDelete={handleDelete} expired={tab === "expired"} />
          ))}
        </div>
      )}
    </>
  );
}
